<template>
  <div class="manage-admin-groups-wrapper">
    <!-- Header - Mobile Optimized -->
    <div class="mobile-header">
      <v-app-bar color="#00479b" dark flat>
        <v-btn icon @click="$router.go(-1)" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="text-h6 font-weight-medium">
          My Admin Groups
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="loadAdminGroups">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-app-bar>
    </div>

    <!-- Main Content -->
    <div class="content-container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state pa-4 text-center">
        <v-progress-circular
          indeterminate
          color="#00479b"
          size="40"
          width="3"
        ></v-progress-circular>
        <p class="mt-3 mb-0" style="color: #666;">Loading your groups...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="adminGroups.length === 0" class="empty-state pa-4">
        <div class="text-center pa-6">
          <v-icon size="80" color="#e0e0e0" class="mb-3">mdi-account-group</v-icon>
          <h3 class="mb-2" style="color: #333;">No Groups Yet</h3>
          <p class="mb-4" style="color: #666;">
            You haven't created any Njangi groups as an administrator.
          </p>
          <v-btn
            color="#00479b"
            dark
            large
            to="/business-account/dashboard/create-njangi-group"
            class="create-btn"
            elevation="0"
          >
            <v-icon left>mdi-plus-circle</v-icon>
            Create First Group
          </v-btn>
        </div>
      </div>

      <!-- Groups Grid -->
      <div v-else class="groups-grid pa-3">
        <div class="section-title pa-3">
          <h3 class="mb-0">
            <v-icon left color="#00479b">mdi-shield-account</v-icon>
            Your Admin Groups
          </h3>
        </div>

        <div class="groups-list">
          <div
            v-for="group in adminGroups"
            :key="group.id"
            class="group-card"
            @click="openManageDialog(group)"
          >
            <!-- Group Header -->
            <div class="group-header" style="background: linear-gradient(135deg, #00479b 0%, #2c6bb5 100%);">
              <div class="group-icon">
                <v-icon color="white">mdi-account-group</v-icon>
              </div>
              <h4 class="group-name white--text">{{ group.name }}</h4>

              <!-- Badge pending -->
              <v-chip
                v-if="group.pending"
                small
                color="warning"
                text-color="white"
                class="mt-2"
              >
                <v-icon x-small left>mdi-pause-circle</v-icon>
                Pending Review
              </v-chip>
            </div>

            <!-- Group Details -->
            <div class="group-details pa-3">
              <!-- Contribution -->
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small color="#00479b" class="mr-2">mdi-currency-usd</v-icon>
                  <span>Contribution</span>
                </div>
                <div class="detail-value">
                  {{ group.contribution_amount.toLocaleString() }} FCFA
                </div>
              </div>

              <!-- Cycle -->
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small color="#00479b" class="mr-2">mdi-calendar-clock</v-icon>
                  <span>Cycle</span>
                </div>
                <div class="detail-value">
                  {{ group.collecting_cycle }}
                </div>
              </div>

              <!-- Members -->
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon small color="#00479b" class="mr-2">mdi-account-multiple</v-icon>
                  <span>Members</span>
                </div>
                <div class="detail-value">
                  {{ group.members_count }} active
                </div>
              </div>

              <!-- Pending Invitations -->
              <div v-if="group.pending_invitations > 0" class="detail-item">
                <div class="detail-label">
                  <v-icon small color="#ff9800" class="mr-2">mdi-account-clock</v-icon>
                  <span>Pending Invitations</span>
                </div>
                <div class="detail-value">
                  <v-chip small color="orange" text-color="white">
                    {{ group.pending_invitations }}
                  </v-chip>
                </div>
              </div>

              <!-- Group Status Information -->
              <div class="detail-item">
                <div class="detail-label">
                  <v-icon
                    small
                    :color="group.pending ? '#ff9800' : '#4caf50'"
                    class="mr-2"
                  >
                    {{ group.pending ? 'mdi-clock-alert' : 'mdi-check-circle' }}
                  </v-icon>
                  <span>Group Status</span>
                </div>
                <div class="detail-value">
                  <div v-if="group.pending" class="pending-info">
                    <v-chip
                      small
                      color="warning"
                      text-color="white"
                    >
                      <v-icon x-small left>mdi-clock</v-icon>
                      Awaiting Manager Activation
                    </v-chip>

                  </div>
                  <v-chip
                    v-else
                    small
                    color="green lighten-4"
                    text-color="green darken-2"
                  >
                    Active
                  </v-chip>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="group-actions pa-3">
              <!-- Information pour les groupes en attente -->
              <div v-if="group.pending" class="pending-notice pa-3 mb-3" style="background-color: #fff3e0; border-radius: 8px;">
                <p class="mb-1" style="color: #e65100; font-size: 0.75rem;">
                  <v-icon x-small color="warning" left>mdi-information</v-icon>
                  Your group is pending review by a manager. Contributions will be enabled after approval.
                </p>
                <p class="mb-2" style="color: #666; font-size: 0.75rem;">
                  Expected review time: 24-48 hours
                </p>
                <v-btn
                  x-small
                  color="primary"
                  @click.stop="openPettyCashChat(group)"
                  block
                >
                  <v-icon x-small left>mdi-chat</v-icon>
                  Ask about approval status
                </v-btn>
              </div>

              <v-btn
                small
                color="#25D366"
                dark
                @click.stop="shareViaWhatsApp(group)"
                block
                class="share-btn mb-2"
                elevation="0"
              >
                <v-icon left small>mdi-whatsapp</v-icon>
                Share
              </v-btn>
              <v-btn
                small
                color="#00479b"
                dark
                block
                class="manage-btn"
                elevation="0"
              >
                <v-icon left small>mdi-cog</v-icon>
                Manage Group
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Group Management Dialog -->
    <v-dialog v-model="showManageDialog" max-width="800px" persistent>
      <div class="dialog-wrapper" v-if="selectedGroup">
        <!-- Dialog Header -->
        <div class="dialog-header" style="background: linear-gradient(135deg, #00479b 0%, #2c6bb5 100%);">
          <div class="d-flex align-center">
            <v-btn icon @click="closeManageDialog" dark class="mr-2">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <h3 class="white--text mb-0">
              <v-icon left color="white">mdi-cog</v-icon>
              {{ selectedGroup.name }}
            </h3>
          </div>
        </div>

        <!-- Tabs -->
        <div class="dialog-tabs">
          <v-tabs v-model="manageTab" center-active class="management-tabs">
            <v-tab class="management-tab">
              <v-icon left small>mdi-account-multiple</v-icon>
              Members
            </v-tab>
            <v-tab class="management-tab">
              <v-icon left small>mdi-sort-numeric-variant</v-icon>
              Positions
            </v-tab>
            <v-tab class="management-tab">
              <v-icon left small>mdi-share-variant</v-icon>
              Invite
            </v-tab>
            <v-tab class="management-tab">
              <v-icon left small>mdi-email-outline</v-icon>
              Invitations
              <v-chip
                v-if="invitationsStats.pending_count > 0"
                x-small
                color="warning"
                text-color="white"
                class="ml-2"
              >
                {{ invitationsStats.pending_count }}
              </v-chip>
            </v-tab>
          </v-tabs>
        </div>

        <!-- Tab Content -->
        <div class="dialog-content">
          <v-tabs-items v-model="manageTab" class="tab-items">
            <!-- Tab 1: Members -->
            <v-tab-item>
              <div class="tab-content pa-3">
                <div class="tab-header mb-3">
                  <h4 class="mb-1">Group Members</h4>
                  <p class="mb-0" style="color: #666; font-size: 0.875rem;">
                    Manage and view all group members
                  </p>
                </div>

                <div v-if="loadingMembers" class="loading-members text-center pa-4">
                  <v-progress-circular
                    indeterminate
                    color="#00479b"
                    size="32"
                    width="2"
                  ></v-progress-circular>
                  <p class="mt-3 mb-0" style="color: #666;">Loading members...</p>
                </div>

                <div v-else>
                  <div v-if="groupMembers.length === 0" class="empty-members pa-4 text-center">
                    <v-icon size="60" color="#e0e0e0" class="mb-3">mdi-account-multiple-outline</v-icon>
                    <h4 class="mb-2" style="color: #666;">No Members Yet</h4>
                    <p class="mb-0" style="color: #999;">Invite members to join this group</p>
                  </div>

                  <div v-else>
                    <div class="members-count mb-3">
                      <v-chip color="#e3f2fd" text-color="#00479b" small>
                        {{ groupMembers.length }} Active Members
                      </v-chip>
                    </div>

                    <!-- Members List -->
                    <div class="members-list">
                      <div
                        v-for="member in groupMembers"
                        :key="member.id"
                        class="member-card pa-3 mb-2"
                      >
                        <div class="d-flex align-center">
                          <!-- Avatar -->
                          <v-avatar size="40" color="#f0f0f0" class="mr-3">
                            <v-icon color="#666" v-if="!member.profile_picture">mdi-account</v-icon>
                            <v-img v-else :src="member.profile_picture"></v-img>
                          </v-avatar>

                          <!-- Member Info -->
                          <div class="flex-grow-1">
                            <div class="d-flex align-center">
                              <span class="member-name font-weight-medium mr-2">{{ member.username }}</span>
                              <v-chip
                                v-if="member.user_id === selectedGroup.admin_id"
                                x-small
                                color="#00479b"
                                text-color="white"
                                class="ml-1"
                              >
                                Admin
                              </v-chip>
                            </div>
                            <div class="member-details mt-1">
                              <v-chip x-small color="#e3f2fd" text-color="#00479b" class="mr-2">
                                Position {{ member.position }}
                              </v-chip>
                              <v-chip
                                v-if="member.unpaid_penalties > 0"
                                x-small
                                color="#ffebee"
                                text-color="#d32f2f"
                              >
                                {{ member.unpaid_penalties }} penalty
                              </v-chip>
                            </div>
                          </div>

                          <!-- Actions -->
                          <v-btn
                            icon
                            small
                            color="#ff5252"
                            @click.stop="confirmRemoveMember(member)"
                            :disabled="member.user_id === selectedGroup.admin_id"
                            class="ml-2"
                          >
                            <v-icon small>mdi-delete-outline</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- Tab 2: Manage Positions -->
            <v-tab-item>
              <div class="tab-content pa-3">
                <!-- En-tête avec titre et compteur -->
                <div class="position-header mb-4">
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div>
                      <h4 class="mb-1 font-weight-bold" style="color: #333; font-size: 1.25rem;">
                        <v-icon color="#00479b" class="mr-2">mdi-sort-numeric-variant</v-icon>
                        Collection Order Management
                      </h4>
                      <p class="mb-0" style="color: #666; font-size: 0.875rem;">
                        Manage the order in which members collect their contributions
                      </p>
                    </div>
                    <v-chip color="#00479b" text-color="white" class="px-3">
                      <v-icon small left>mdi-account-multiple</v-icon>
                      <strong>{{ groupMembers.length }}</strong>
                      <span class="ml-1">Members</span>
                    </v-chip>
                  </div>

                  <!-- Instructions card -->
                  <v-card flat class="instruction-card pa-3 mb-3" style="background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%); border-left: 4px solid #00479b; border-radius: 8px;">
                    <div class="d-flex align-start">
                      <v-icon color="#00479b" class="mr-3" size="20">mdi-lightbulb-on</v-icon>
                      <div style="flex: 1;">
                        <p class="mb-2" style="color: #00479b; font-weight: 500; font-size: 0.9rem;">
                          How to manage positions:
                        </p>
                        <div style="color: #00479b; font-size: 0.85rem; line-height: 1.6;">
                          • <strong>Click on position numbers</strong> to change member positions<br>
                          • <strong>Use the menu (⋮)</strong> to access more options<br>
                          • <strong>Green badge</strong> indicates the current collector
                        </div>
                      </div>
                    </div>
                  </v-card>
                </div>

                <div v-if="loadingMembers" class="loading-members text-center pa-4">
                  <v-progress-circular
                    indeterminate
                    color="#00479b"
                    size="32"
                    width="2"
                  ></v-progress-circular>
                  <p class="mt-3 mb-0" style="color: #666;">Loading positions...</p>
                </div>

                <div v-else>
                  <!-- Liste des membres avec leurs positions -->
                  <v-list class="transparent">
                    <v-list-item
                      v-for="(member, index) in sortedMemberPositions"
                      :key="member.id"
                      class="elevation-2 mb-3 rounded-lg position-item pa-2"
                      style="background: white; border: 1px solid #e0e0e0;"
                    >
                      <!-- Numéro de position (cliquable) -->
                      <v-list-item-avatar style="margin-right: 16px;">
                        <v-chip
                          large
                          :color="member.position === currentCollectPosition ? 'success' : '#00479b'"
                          text-color="white"
                          style="cursor: pointer; min-width: 48px; justify-content: center;"
                          @click="openPositionDialog(member)"
                        >
                          <span class="font-weight-bold" style="font-size: 1.1rem;">{{ member.position }}</span>
                        </v-chip>
                      </v-list-item-avatar>

                      <!-- Informations du membre -->
                      <v-list-item-content>
                        <v-list-item-title class="mb-2">
                          <div class="d-flex align-center">
                            <v-avatar color="#00479b" size="36" class="mr-2">
                              <span class="white--text font-weight-bold">
                                {{ member.username && member.username.charAt(0).toUpperCase() }}
                              </span>
                            </v-avatar>
                            <span class="font-weight-bold" style="font-size: 1rem; color: #333;">
                              {{ member.username }}
                            </span>

                            <!-- Admin badge -->
                            <v-chip
                              v-if="member.user_id === selectedGroup.admin_id"
                              x-small
                              color="#00479b"
                              text-color="white"
                              class="ml-2"
                            >
                              <v-icon x-small left>mdi-shield-check</v-icon>
                              Admin
                            </v-chip>

                            <!-- Current collector badge -->
                            <v-chip
                              v-if="member.position === currentCollectPosition"
                              x-small
                              color="success"
                              text-color="white"
                              class="ml-2"
                            >
                              <v-icon x-small left>mdi-star</v-icon>
                              Current Collector
                            </v-chip>
                          </div>
                        </v-list-item-title>

                        <v-list-item-subtitle style="color: #666; font-size: 0.85rem;">
                          <v-icon x-small color="#666" class="mr-1">mdi-map-marker</v-icon>
                          Position {{ member.position }}
                          <span class="mx-2">•</span>
                          <v-icon x-small color="#666" class="mr-1">mdi-check-circle</v-icon>
                          {{ member.status || 'Active' }}
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <!-- Menu d'actions -->
                      <v-list-item-action>
                        <v-menu bottom left offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on" color="#666">
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item @click="openPositionDialog(member)" class="px-3">
                              <v-list-item-icon class="mr-3">
                                <v-icon color="#00479b" size="20">mdi-swap-horizontal</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title style="font-size: 0.9rem;">Change Position</v-list-item-title>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item
                              @click="confirmRemoveMemberFromPosition(member)"
                              :disabled="member.user_id === selectedGroup.admin_id"
                              class="px-3"
                            >
                              <v-list-item-icon class="mr-3">
                                <v-icon color="error" size="20">mdi-account-minus</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title style="font-size: 0.9rem; color: #f44336;">Remove Member</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </div>
              </div>
            </v-tab-item>

            <!-- Tab 3: Invite Link -->
            <v-tab-item>
              <div class="tab-content pa-3">
                <div class="tab-header mb-3">
                  <h4 class="mb-1">Invite Members</h4>
                  <p class="mb-0" style="color: #666; font-size: 0.875rem;">
                    Share the group link or invite specific users
                  </p>
                </div>

                <!-- Group Status Notice -->
                <div v-if="selectedGroup.pending" class="pending-notice-large pa-3 mb-4" style="background-color: #fff3e0; border-radius: 8px; border-left: 4px solid #ff9800;">
                  <div class="d-flex align-start">
                    <v-icon color="#ff9800" class="mr-2">mdi-information</v-icon>
                    <div>
                      <p class="mb-1" style="color: #e65100; font-weight: 500;">
                        <v-icon x-small color="warning" left>mdi-clock-alert</v-icon>
                        Group Pending Activation
                      </p>
                      <p class="mb-2" style="color: #666; font-size: 0.875rem;">
                        Your group is currently under review by a manager. Members can join, but contributions will only begin after approval.
                      </p>
                      <v-btn
                        x-small
                        color="primary"
                        @click="openPettyCashChat(selectedGroup)"
                        class="mt-1"
                      >
                        <v-icon x-small left>mdi-chat</v-icon>
                        Contact Support
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- Invite Link -->
                <div class="invite-link-section mb-4">
                  <label class="form-label mb-2">Invitation Link</label>
                  <div class="link-input-wrapper">
                    <input
                      :value="selectedGroup.invite_link"
                      readonly
                      class="link-input"
                      @click="copyInviteLink(selectedGroup.invite_link)"
                    />
                    <v-btn
                      icon
                      @click="copyInviteLink(selectedGroup.invite_link)"
                      color="#00479b"
                      class="copy-btn"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                  <p class="caption mt-1" style="color: #666;">
                    Tap to copy the link
                  </p>
                </div>

                <!-- WhatsApp Share -->
                <div class="share-section mb-4">
                  <v-btn
                    block
                    large
                    color="#25D366"
                    dark
                    @click="shareViaWhatsApp(selectedGroup)"
                    class="whatsapp-share-btn"
                    elevation="0"
                  >
                    <v-icon left>mdi-whatsapp</v-icon>
                    Share via WhatsApp
                  </v-btn>
                </div>

                <v-divider class="my-4"></v-divider>

                <!-- Search Users -->
                <div class="search-users-section">
                  <h5 class="mb-3" style="color: #333;">Invite Specific Users</h5>
                  <v-text-field
                    v-model="searchUsername"
                    placeholder="Search by username or email"
                    outlined
                    dense
                    @input="searchUsers"
                    clearable
                    hide-details
                    class="mb-3"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon color="#666">mdi-magnify</v-icon>
                    </template>
                  </v-text-field>

                  <!-- Search Results -->
                  <div v-if="searchResults.length > 0" class="search-results">
                    <div
                      v-for="user in searchResults"
                      :key="user.id"
                      class="user-result pa-3 mb-2"
                      @click="inviteUser(user)"
                    >
                      <div class="d-flex align-center">
                        <v-avatar size="36" color="#f0f0f0" class="mr-3">
                          <v-icon color="#666">mdi-account</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="user-name font-weight-medium">{{ user.username }}</div>
                          <div v-if="user.phone_number" class="user-phone caption" style="color: #666;">
                            {{ user.phone_number }}
                          </div>
                        </div>
                        <v-icon color="#00479b">mdi-plus-circle</v-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- Tab 4: Invitations Status -->
            <v-tab-item>
              <div class="tab-content pa-3">
                <div class="tab-header mb-3">
                  <h4 class="mb-1">Invitations Status</h4>
                  <p class="mb-0" style="color: #666; font-size: 0.875rem;">
                    View and manage all group invitations
                  </p>
                </div>

                <!-- Group Status Notice -->
                <div v-if="selectedGroup.pending" class="pending-notice-large pa-3 mb-4" style="background-color: #fff3e0; border-radius: 8px;">
                  <div class="d-flex align-center">
                    <v-icon color="warning" class="mr-2">mdi-clock-alert</v-icon>
                    <div>
                      <p class="mb-0" style="color: #e65100;">
                        Group pending activation by manager. Contributions will be enabled after approval.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Loading State -->
                <div v-if="loadingInvitations" class="loading-invitations text-center pa-4">
                  <v-progress-circular
                    indeterminate
                    color="#00479b"
                    size="32"
                    width="2"
                  ></v-progress-circular>
                  <p class="mt-3 mb-0" style="color: #666;">Loading invitations...</p>
                </div>

                <div v-else>
                  <!-- Statistics Cards -->
                  <v-layout row wrap class="mb-4">
                    <v-flex xs6 sm3 class="pa-2">
                      <div class="stat-card" style="background: #e3f2fd; border-radius: 8px; padding: 12px;">
                        <div class="stat-icon text-center">
                          <v-icon color="#00479b" size="24">mdi-email-outline</v-icon>
                        </div>
                        <div class="stat-value text-center" style="font-size: 1.5rem; font-weight: bold; color: #00479b;">
                          {{ invitationsStats.total }}
                        </div>
                        <div class="stat-label text-center" style="font-size: 0.75rem; color: #666;">
                          Total
                        </div>
                      </div>
                    </v-flex>

                    <v-flex xs6 sm3 class="pa-2">
                      <div class="stat-card" style="background: #fff3e0; border-radius: 8px; padding: 12px;">
                        <div class="stat-icon text-center">
                          <v-icon color="#ff9800" size="24">mdi-clock-outline</v-icon>
                        </div>
                        <div class="stat-value text-center" style="font-size: 1.5rem; font-weight: bold; color: #ff9800;">
                          {{ invitationsStats.pending_count }}
                        </div>
                        <div class="stat-label text-center" style="font-size: 0.75rem; color: #666;">
                          Pending
                        </div>
                      </div>
                    </v-flex>

                    <v-flex xs6 sm3 class="pa-2">
                      <div class="stat-card" style="background: #e8f5e9; border-radius: 8px; padding: 12px;">
                        <div class="stat-icon text-center">
                          <v-icon color="#4caf50" size="24">mdi-check-circle</v-icon>
                        </div>
                        <div class="stat-value text-center" style="font-size: 1.5rem; font-weight: bold; color: #4caf50;">
                          {{ invitationsStats.active_count }}
                        </div>
                        <div class="stat-label text-center" style="font-size: 0.75rem; color: #666;">
                          Active
                        </div>
                      </div>
                    </v-flex>

                    <v-flex xs6 sm3 class="pa-2">
                      <div class="stat-card" style="background: #ffebee; border-radius: 8px; padding: 12px;">
                        <div class="stat-icon text-center">
                          <v-icon color="#f44336" size="24">mdi-close-circle</v-icon>
                        </div>
                        <div class="stat-value text-center" style="font-size: 1.5rem; font-weight: bold; color: #f44336;">
                          {{ invitationsStats.cancelled_count }}
                        </div>
                        <div class="stat-label text-center" style="font-size: 0.75rem; color: #666;">
                          Cancelled
                        </div>
                      </div>
                    </v-flex>
                  </v-layout>

                  <!-- Invitation Tabs -->
                  <v-tabs v-model="selectedInvitationTab" class="invitations-tabs mb-3">
                    <v-tab>
                      Pending
                      <v-chip v-if="invitationsStats.pending_count > 0" x-small color="warning" text-color="white" class="ml-2">
                        {{ invitationsStats.pending_count }}
                      </v-chip>
                    </v-tab>
                    <v-tab>
                      Active
                      <v-chip v-if="invitationsStats.active_count > 0" x-small color="success" text-color="white" class="ml-2">
                        {{ invitationsStats.active_count }}
                      </v-chip>
                    </v-tab>
                    <v-tab>Cancelled</v-tab>
                    <v-tab>Interrupted</v-tab>
                  </v-tabs>

                  <!-- Tab Items -->
                  <v-tabs-items v-model="selectedInvitationTab">
                    <!-- Pending Invitations -->
                    <v-tab-item>
                      <div v-if="invitationsData.pending.length === 0" class="empty-state pa-4 text-center">
                        <v-icon size="60" color="#e0e0e0" class="mb-3">mdi-email-check-outline</v-icon>
                        <p class="mb-0" style="color: #666;">No pending invitations</p>
                      </div>
                      <div v-else class="invitations-list">
                        <div
                          v-for="invitation in invitationsData.pending"
                          :key="invitation.id"
                          class="invitation-card pa-3 mb-2"
                          style="background: #fff3e0; border-left: 4px solid #ff9800; border-radius: 8px;"
                        >
                          <v-layout row wrap align-center>
                            <v-flex xs12 sm8>
                              <div class="d-flex align-center">
                                <v-avatar size="36" color="#f0f0f0" class="mr-3">
                                  <v-icon color="#666">mdi-account</v-icon>
                                </v-avatar>
                                <div>
                                  <div class="font-weight-medium">{{ invitation.username }}</div>
                                  <div class="caption" style="color: #666;">
                                    <v-icon x-small>mdi-email</v-icon>
                                    {{ invitation.email }}
                                  </div>
                                  <div class="caption" style="color: #999;">
                                    Invited: {{ invitation.invited_at }}
                                  </div>
                                </div>
                              </div>
                            </v-flex>
                            <v-flex xs12 sm4 class="text-xs-right mt-2 mt-sm-0">
                              <v-btn
                                x-small
                                color="primary"
                                @click="resendInvitation(invitation)"
                                :loading="resendingInvitation"
                                class="mr-2"
                              >
                                <v-icon x-small left>mdi-send</v-icon>
                                Resend
                              </v-btn>
                              <v-btn
                                x-small
                                color="error"
                                @click="cancelInvitation(invitation)"
                                :loading="cancellingInvitation"
                              >
                                <v-icon x-small left>mdi-close</v-icon>
                                Cancel
                              </v-btn>
                            </v-flex>
                          </v-layout>
                        </div>
                      </div>
                    </v-tab-item>

                    <!-- Active Members -->
                    <v-tab-item>
                      <div v-if="invitationsData.active.length === 0" class="empty-state pa-4 text-center">
                        <v-icon size="60" color="#e0e0e0" class="mb-3">mdi-account-check</v-icon>
                        <p class="mb-0" style="color: #666;">No active members</p>
                      </div>
                      <div v-else class="invitations-list">
                        <div
                          v-for="invitation in invitationsData.active"
                          :key="invitation.id"
                          class="invitation-card pa-3 mb-2"
                          style="background: #e8f5e9; border-left: 4px solid #4caf50; border-radius: 8px;"
                        >
                          <v-layout row wrap align-center>
                            <v-flex xs12>
                              <div class="d-flex align-center">
                                <v-avatar size="36" color="#f0f0f0" class="mr-3">
                                  <v-icon color="#666">mdi-account</v-icon>
                                </v-avatar>
                                <div class="flex-grow-1">
                                  <div class="font-weight-medium">{{ invitation.username }}</div>
                                  <div class="caption" style="color: #666;">
                                    Position {{ invitation.position }} • Joined: {{ invitation.invited_at }}
                                  </div>
                                </div>
                                <v-chip small color="success" text-color="white">
                                  <v-icon x-small left>mdi-check-circle</v-icon>
                                  Active
                                </v-chip>
                              </div>
                            </v-flex>
                          </v-layout>
                        </div>
                      </div>
                    </v-tab-item>

                    <!-- Cancelled Invitations -->
                    <v-tab-item>
                      <div v-if="invitationsData.cancelled.length === 0" class="empty-state pa-4 text-center">
                        <v-icon size="60" color="#e0e0e0" class="mb-3">mdi-cancel</v-icon>
                        <p class="mb-0" style="color: #666;">No cancelled invitations</p>
                      </div>
                      <div v-else class="invitations-list">
                        <div
                          v-for="invitation in invitationsData.cancelled"
                          :key="invitation.id"
                          class="invitation-card pa-3 mb-2"
                          style="background: #ffebee; border-left: 4px solid #f44336; border-radius: 8px;"
                        >
                          <v-layout row wrap align-center>
                            <v-flex xs12 sm8>
                              <div class="d-flex align-center">
                                <v-avatar size="36" color="#f0f0f0" class="mr-3">
                                  <v-icon color="#666">mdi-account</v-icon>
                                </v-avatar>
                                <div>
                                  <div class="font-weight-medium">{{ invitation.username }}</div>
                                  <div class="caption" style="color: #999;">
                                    Cancelled: {{ invitation.invited_at }}
                                  </div>
                                </div>
                              </div>
                            </v-flex>
                            <v-flex xs12 sm4 class="text-xs-right mt-2 mt-sm-0">
                              <v-btn
                                x-small
                                color="primary"
                                @click="resendInvitation(invitation)"
                                :loading="resendingInvitation"
                              >
                                <v-icon x-small left>mdi-restore</v-icon>
                                Resend
                              </v-btn>
                            </v-flex>
                          </v-layout>
                        </div>
                      </div>
                    </v-tab-item>

                    <!-- Interrupted -->
                    <v-tab-item>
                      <div v-if="invitationsData.interrupted.length === 0" class="empty-state pa-4 text-center">
                        <v-icon size="60" color="#e0e0e0" class="mb-3">mdi-pause-circle-outline</v-icon>
                        <p class="mb-0" style="color: #666;">No interrupted memberships</p>
                      </div>
                      <div v-else class="invitations-list">
                        <div
                          v-for="invitation in invitationsData.interrupted"
                          :key="invitation.id"
                          class="invitation-card pa-3 mb-2"
                          style="background: #f5f5f5; border-left: 4px solid #9e9e9e; border-radius: 8px;"
                        >
                          <v-layout row wrap align-center>
                            <v-flex xs12>
                              <div class="d-flex align-center">
                                <v-avatar size="36" color="#f0f0f0" class="mr-3">
                                  <v-icon color="#666">mdi-account</v-icon>
                                </v-avatar>
                                <div class="flex-grow-1">
                                  <div class="font-weight-medium">{{ invitation.username }}</div>
                                  <div class="caption" style="color: #666;">
                                    Interrupted: {{ invitation.invited_at }}
                                  </div>
                                </div>
                                <v-chip small color="grey" text-color="white">
                                  <v-icon x-small left>mdi-pause</v-icon>
                                  Interrupted
                                </v-chip>
                              </div>
                            </v-flex>
                          </v-layout>
                        </div>
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </div>
    </v-dialog>

    <!-- Dialog pour changer la position d'un membre -->
    <v-dialog v-model="positionDialog" max-width="500px">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-swap-horizontal</v-icon>
          Change Member Position
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Informations du membre -->
          <div class="text-center mb-4">
            <v-avatar color="primary" size="80" class="mb-2">
              <span class="white--text text-h5">
                {{ selectedMember && selectedMember.username && selectedMember.username.charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <h3 class="font-weight-bold">{{ selectedMember && selectedMember.username }}</h3>
            <p class="grey--text">Current Position: {{ selectedMember && selectedMember.position }}</p>
          </div>

          <!-- Input pour nouvelle position -->
          <v-text-field
            v-model="newPosition"
            label="New Position"
            type="number"
            :min="1"
            :max="groupMembers.length"
            outlined
            dense
            prepend-inner-icon="mdi-sort-numeric-variant"
            :error="!!positionError"
            :error-messages="positionError"
            class="mb-2"
          ></v-text-field>

          <!-- Alert pour échange de positions -->
          <v-alert v-if="newPosition && getPositionTaker()" type="info" dense>
            <small>
              <v-icon small left>mdi-information</v-icon>
              Position {{ newPosition }} is currently held by <strong>{{ getPositionTaker() && getPositionTaker().username }}</strong>.
              Their positions will be swapped automatically.
            </small>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closePositionDialog">
            <v-icon left small>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="updateMemberPosition"
            :loading="updatingPosition"
            :disabled="!newPosition || newPosition == (selectedMember && selectedMember.position)"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Update Position
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Member Dialog (from positions tab) -->
    <v-dialog v-model="showRemoveMemberPositionDialog" max-width="550px">
      <v-card>
        <v-card-title class="error white--text justify-center">
          <v-icon left color="white">mdi-account-minus</v-icon>
          Remove Member from Group
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Informations du membre à supprimer -->
          <div class="text-center mb-4">
            <v-avatar color="error" size="80" class="mb-2">
              <span class="white--text text-h5">
                {{ memberToRemovePosition && memberToRemovePosition.username && memberToRemovePosition.username.charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <h3 class="font-weight-bold">{{ memberToRemovePosition && memberToRemovePosition.username }}</h3>
            <p class="grey--text">Position: {{ memberToRemovePosition && memberToRemovePosition.position }}</p>
          </div>

          <!-- Alertes d'avertissement -->
          <v-alert type="warning" dense class="mb-3">
            <div class="font-weight-bold mb-2">
              <v-icon small left>mdi-alert</v-icon>
              This action will:
            </div>
            <ul class="ml-4">
              <li>Remove the member from the group permanently</li>
              <li>Adjust all subsequent member positions (-1)</li>
              <li>Update the current collection order if needed</li>
              <li>Cancel any pending transactions for this member</li>
            </ul>
          </v-alert>

          <v-alert type="error" dense>
            <strong>
              <v-icon small left>mdi-alert-circle</v-icon>
              This action cannot be undone!
            </strong>
          </v-alert>
        </v-card-text>

        <v-card-actions class="justify-center pb-4">
          <v-btn color="grey" text @click="showRemoveMemberPositionDialog = false" class="mr-4">
            <v-icon left small>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn color="error" @click="removeMemberFromPosition" :loading="removingMemberPosition">
            <v-icon left small>mdi-account-minus</v-icon>
            Remove Member
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Member Dialog (from members tab) -->
    <v-dialog v-model="showRemoveDialog" max-width="400px" persistent>
      <div class="remove-dialog">
        <!-- Header -->
        <div class="dialog-header" style="background: linear-gradient(135deg, #d32f2f 0%, #ef5350 100%);">
          <h3 class="white--text mb-0">
            <v-icon left color="white">mdi-alert-circle</v-icon>
            Remove Member
          </h3>
        </div>

        <!-- Content -->
        <div class="dialog-content pa-4">
          <div class="warning-message pa-3 mb-4" style="background-color: #ffebee; border-radius: 8px;">
            <div class="d-flex align-start">
              <v-icon color="#d32f2f" class="mr-2 mt-1">mdi-alert</v-icon>
              <div>
                <p class="mb-1" style="color: #333;">
                  Are you sure you want to remove <strong>{{ memberToRemove?.username }}</strong>?
                </p>
                <p class="mb-0" style="font-size: 0.875rem; color: #666;">
                  This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="dialog-actions pa-4">
          <v-btn
            block
            large
            color="#e0e0e0"
            @click="showRemoveDialog = false"
            class="mr-2"
            elevation="0"
          >
            Cancel
          </v-btn>
          <v-btn
            block
            large
            color="#d32f2f"
            dark
            @click="removeMember"
            :loading="removingMember"
            elevation="0"
          >
            <v-icon left>mdi-delete</v-icon>
            Remove
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ManageAdminGroups',

  data() {
    return {
      loading: false,
      adminGroups: [],
      showManageDialog: false,
      selectedGroup: null,
      manageTab: 0,

      // Members
      loadingMembers: false,
      groupMembers: [],
      sortableMembers: [],
      originalPositions: [],
      reorderingPositions: false,

      // Removal
      showRemoveDialog: false,
      memberToRemove: null,
      removingMember: false,

      // Search and invitation
      searchUsername: '',
      searchResults: [],

      // Gestion des invitations
      loadingInvitations: false,
      invitationsData: {
        pending: [],
        active: [],
        cancelled: [],
        interrupted: []
      },
      invitationsStats: {
        total: 0,
        pending_count: 0,
        active_count: 0,
        cancelled_count: 0,
        interrupted_count: 0
      },
      selectedInvitationTab: 0,
      cancellingInvitation: false,
      resendingInvitation: false,

      // Nouveau système de gestion des positions
      positionDialog: false,
      selectedMember: null,
      newPosition: null,
      positionError: "",
      updatingPosition: false,
      currentCollectPosition: null,

      // Suppression depuis l'onglet positions
      showRemoveMemberPositionDialog: false,
      memberToRemovePosition: null,
      removingMemberPosition: false
    };
  },

  computed: {
    sortedMemberPositions() {
      return [...this.groupMembers].sort((a, b) => a.position - b.position);
    }
  },

  watch: {
    showManageDialog(newVal) {
      if (!newVal) {
        this.resetManageDialog();
      }
    },
    manageTab(newTab) {
      if ((newTab === 0 || newTab === 1) && this.selectedGroup && this.groupMembers.length === 0) {
        this.loadGroupMembers();
      }
      if (newTab === 3 && this.selectedGroup) {
        this.loadGroupInvitations();
      }
    }
  },

  mounted() {
    this.loadAdminGroups();
  },

  methods: {
    async loadAdminGroups() {
      this.loading = true;
      try {
        const response = await axios.get('/api/v1/njangi/admin/groups/');
        this.adminGroups = response.data;
      } catch (error) {
        console.error('Load groups error:', error);
        this.showSnackbar('Error loading groups', 'error');
      } finally {
        this.loading = false;
      }
    },

    async openManageDialog(group) {
      this.selectedGroup = group;
      this.showManageDialog = true;
      this.manageTab = 0;

      // Reset
      this.groupMembers = [];
      this.sortableMembers = [];
      this.originalPositions = [];
      this.invitationsData = { pending: [], active: [], cancelled: [], interrupted: [] };
      this.invitationsStats = { total: 0, pending_count: 0, active_count: 0, cancelled_count: 0, interrupted_count: 0 };

      await this.loadGroupMembers();
      await this.loadGroupInvitations();
    },

    closeManageDialog() {
      this.showManageDialog = false;
      this.resetManageDialog();
    },

    resetManageDialog() {
      setTimeout(() => {
        this.selectedGroup = null;
        this.groupMembers = [];
        this.sortableMembers = [];
        this.originalPositions = [];
        this.manageTab = 0;
        this.searchUsername = '';
        this.searchResults = [];
        this.invitationsData = { pending: [], active: [], cancelled: [], interrupted: [] };
        this.invitationsStats = { total: 0, pending_count: 0, active_count: 0, cancelled_count: 0, interrupted_count: 0 };
        this.currentCollectPosition = null;
      }, 300);
    },

    async loadGroupMembers() {
      if (!this.selectedGroup) return;

      this.loadingMembers = true;

      try {
        const response = await axios.get(`/api/v1/njangi/groups/${this.selectedGroup.id}/members/`);

        if (Array.isArray(response.data)) {
          this.groupMembers = response.data.map(member => ({
            ...member,
            position_name: member.position_name || `Position ${member.position}`,
            unpaid_penalties: member.unpaid_penalties || 0,
            status: member.status || 'Active'
          }));

          this.sortableMembers = [...this.groupMembers];
          this.originalPositions = [...this.groupMembers];

          // Charger la position actuelle du collecteur
          await this.loadCurrentCollectPosition();
        }
      } catch (error) {
        console.error('Load members error:', error);
        this.showSnackbar('Error loading members', 'error');
      } finally {
        this.loadingMembers = false;
      }
    },

    async loadCurrentCollectPosition() {
      try {
        const response = await axios.get(`/api/v1/njangi/groups/${this.selectedGroup.id}/current-collector/`);
        this.currentCollectPosition = response.data.current_position;
      } catch (error) {
        console.error('Error loading current collector position:', error);
      }
    },

    async loadGroupInvitations() {
      if (!this.selectedGroup) return;

      this.loadingInvitations = true;

      try {
        const response = await axios.get(
          `/api/v1/njangi/groups/${this.selectedGroup.id}/invitations/`
        );

        this.invitationsData = response.data.invitations;
        this.invitationsStats = response.data.stats;

      } catch (error) {
        console.error('Load invitations error:', error);
        this.showSnackbar('Error loading invitations', 'error');
      } finally {
        this.loadingInvitations = false;
      }
    },

    async cancelInvitation(invitation) {
      if (!confirm(`Cancel invitation for ${invitation.username}?`)) return;

      this.cancellingInvitation = true;

      try {
        const response = await axios.delete(
          `/api/v1/njangi/groups/${this.selectedGroup.id}/invitations/${invitation.id}/cancel/`
        );

        if (response.data.success) {
          this.showSnackbar(response.data.message, 'success');
          await this.loadGroupInvitations();
        }
      } catch (error) {
        console.error('Cancel invitation error:', error);
        const errorMsg = error.response?.data?.error || 'Error cancelling invitation';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.cancellingInvitation = false;
      }
    },

    async resendInvitation(invitation) {
      this.resendingInvitation = true;

      try {
        const response = await axios.post(
          `/api/v1/njangi/groups/${this.selectedGroup.id}/invitations/${invitation.id}/resend/`
        );

        if (response.data.success) {
          this.showSnackbar(response.data.message, 'success');
        }
      } catch (error) {
        console.error('Resend invitation error:', error);
        const errorMsg = error.response?.data?.error || 'Error resending invitation';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.resendingInvitation = false;
      }
    },

    // Nouveau système de gestion des positions
    openPositionDialog(member) {
      this.selectedMember = member;
      this.newPosition = member.position;
      this.positionError = "";
      this.positionDialog = true;
    },

    closePositionDialog() {
      this.positionDialog = false;
      this.selectedMember = null;
      this.newPosition = null;
      this.positionError = "";
    },

    getPositionTaker() {
      if (!this.newPosition) return null;
      return this.groupMembers.find(m =>
        m.position === parseInt(this.newPosition) && m.id !== this.selectedMember?.id
      );
    },

    async updateMemberPosition() {
      // Validation
      if (!this.newPosition || this.newPosition < 1 || this.newPosition > this.groupMembers.length) {
        this.positionError = `Position must be between 1 and ${this.groupMembers.length}`;
        return;
      }

      this.positionError = "";
      this.updatingPosition = true;

      try {
        const response = await axios.post('/api/v1/manager/njangi/update-member-position/', {
          group_id: this.selectedGroup.id,
          member_id: this.selectedMember.user_id,
          old_position: this.selectedMember.position,
          new_position: parseInt(this.newPosition)
        });

        this.showSnackbar(response.data.message || "Position updated successfully", 'success');

        // Refresh data
        await this.loadGroupMembers();

        this.closePositionDialog();

      } catch (error) {
        this.positionError = error.response?.data?.error || "Error updating position";
        this.showSnackbar(this.positionError, 'error');
      } finally {
        this.updatingPosition = false;
      }
    },

    confirmRemoveMemberFromPosition(member) {
      this.memberToRemovePosition = member;
      this.showRemoveMemberPositionDialog = true;
    },

    async removeMemberFromPosition() {
      this.removingMemberPosition = true;

      try {
        const response = await axios.post('/api/v1/manager/njangi/remove-member/', {
          group_id: this.selectedGroup.id,
          member_id: this.memberToRemovePosition.user_id,
          position: this.memberToRemovePosition.position
        });

        this.showSnackbar(response.data.message || "Member removed successfully", 'success');

        await this.loadGroupMembers();

        this.showRemoveMemberPositionDialog = false;
        this.memberToRemovePosition = null;

      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error removing member';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.removingMemberPosition = false;
      }
    },

    confirmRemoveMember(member) {
      this.memberToRemove = member;
      this.showRemoveDialog = true;
    },

    async removeMember() {
      this.removingMember = true;
      try {
        await axios.delete(`/api/v1/njangi/groups/${this.selectedGroup.id}/members/${this.memberToRemove.user_id}/`);

        this.showSnackbar('Member removed successfully', 'success');
        this.showRemoveDialog = false;

        await this.loadGroupMembers();
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error removing member';
        this.showSnackbar(errorMsg, 'error');
        console.error('Remove member error:', error);
      } finally {
        this.removingMember = false;
        this.memberToRemove = null;
      }
    },

    async searchUsers() {
      if (!this.searchUsername || this.searchUsername.length < 2) {
        this.searchResults = [];
        return;
      }

      try {
        const response = await axios.get('/api/v1/njangi/search-users/', {
          params: { q: this.searchUsername }
        });
        this.searchResults = response.data;
      } catch (error) {
        console.error('Search error:', error);
        this.searchResults = [];
      }
    },

    async inviteUser(user) {
      try {
        await axios.post(`/api/v1/njangi/groups/${this.selectedGroup.id}/invite/`, {
          user_id: user.id,
          username: user.username
        });

        this.showSnackbar(`Invitation sent to ${user.username}`, 'success');
        this.searchUsername = '';
        this.searchResults = [];
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error sending invitation';
        this.showSnackbar(errorMsg, 'error');
      }
    },

    copyInviteLink(link) {
      navigator.clipboard.writeText(link);
      this.showSnackbar('Link copied to clipboard', 'success');
    },

    shareViaWhatsApp(group) {
      const message = `🎯 Join my Njangi group *${group.name}* on PettyCash!\n\n💰 Contribution: ${group.contribution_amount.toLocaleString()} FCFA\n📅 Cycle: ${group.collecting_cycle}\n\n👉 Click here to join: ${group.invite_link}`;
      const whatsappLink = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappLink, '_blank');
    },

    // Méthode pour ouvrir le chat PettyCash
    openPettyCashChat(group) {
      this.$root.$emit('open-chatbot', {
        groupId: group.id,
        autoMessage: `Hello, I have a question about my Njangi group "${group.name}" (ID: ${group.id}). The group is currently pending activation by a manager. Can you provide an update on the approval status?`
      });
    },

    showSnackbar(message, type = 'info') {
      try {
        this.$store.dispatch('snackbar/showMessage', {
          text: message,
          color: type === 'success' ? '#00479b' : '#d32f2f'
        });
      } catch (e) {
        if (this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: message,
            type: type
          });
        }
        console.log(`${type.toUpperCase()}: ${message}`);
      }
    }
  }
};
</script>

<style scoped>
.manage-admin-groups-wrapper {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
  min-height: 100vh;
}

.mobile-header {
  position: sticky;
  top: 0;
  z-index: 100;
}

.content-container {
  max-width: 1200px;
  margin: 0 auto;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.section-title {
  padding: 16px;
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 71, 155, 0.1);
}

.groups-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

@media (max-width: 600px) {
  .groups-list {
    grid-template-columns: 1fr;
  }
}

.group-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.group-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 71, 155, 0.15);
}

.group-header {
  padding: 20px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.group-icon {
  margin-bottom: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
}

.group-name {
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.group-details {
  padding: 16px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.875rem;
}

.detail-value {
  font-weight: 500;
  color: #333;
}

.pending-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.group-actions {
  background-color: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

.pending-notice {
  margin-bottom: 12px;
}

.share-btn,
.manage-btn {
  border-radius: 8px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

.create-btn {
  border-radius: 12px;
  text-transform: none;
  letter-spacing: normal;
}

/* Dialog Styles */
.dialog-wrapper {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  padding: 20px;
  color: white;
}

.dialog-tabs {
  background: white;
  padding: 0 20px;
}

.management-tabs >>> .v-tabs-bar {
  background-color: transparent;
  height: 48px;
}

.management-tab {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  color: #666;
  min-width: 0;
  flex: 1;
}

.management-tab.v-tab--active {
  color: #00479b;
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  background: #f8f9fa;
}

.tab-items {
  background: transparent;
}

.tab-content {
  background: white;
  min-height: 400px;
  border-radius: 12px;
  margin: 16px;
}

.tab-header {
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.pending-notice-large {
  margin-bottom: 16px;
}

.members-list {
  max-height: 400px;
  overflow-y: auto;
}

.member-card {
  background: #f8f9fa;
  border-radius: 10px;
  transition: all 0.2s;
}

.member-card:hover {
  background: #f0f7ff;
  border: 1px solid #e3f2fd;
}

.member-name {
  color: #333;
}

/* Styles pour la liste des positions */
.position-header {
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 16px;
}

.instruction-card {
  transition: all 0.3s ease;
}

.instruction-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.15);
}

.position-item {
  transition: all 0.3s ease;
  background: white;
}

.position-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 71, 155, 0.2) !important;
  border-color: #00479b !important;
}

.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Invite Link Styles */
.invite-link-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 10px;
}

.link-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.link-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  font-size: 0.875rem;
  color: #333;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-input:focus {
  outline: none;
  border-color: #00479b;
}

.copy-btn {
  position: absolute;
  right: 8px;
  background: white;
}

.whatsapp-share-btn {
  border-radius: 10px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

.search-results {
  max-height: 200px;
  overflow-y: auto;
}

.user-result {
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.user-result:hover {
  background: #f0f7ff;
  border: 1px solid #e3f2fd;
}

/* Invitations Styles */
.invitations-list {
  max-height: 400px;
  overflow-y: auto;
}

.invitation-card {
  transition: all 0.2s;
}

.invitation-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Remove Dialog */
.remove-dialog {
  background: white;
  border-radius: 16px;
  overflow: hidden;
}

.remove-dialog .dialog-header {
  padding: 20px;
}

.remove-dialog .dialog-content {
  background: white;
}

.remove-dialog .dialog-actions {
  display: flex;
  gap: 12px;
  background: #f8f9fa;
}

/* Custom Scrollbar */
.members-list::-webkit-scrollbar,
.search-results::-webkit-scrollbar,
.invitations-list::-webkit-scrollbar {
  width: 6px;
}

.members-list::-webkit-scrollbar-track,
.search-results::-webkit-scrollbar-track,
.invitations-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.members-list::-webkit-scrollbar-thumb,
.search-results::-webkit-scrollbar-thumb,
.invitations-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.members-list::-webkit-scrollbar-thumb:hover,
.search-results::-webkit-scrollbar-thumb:hover,
.invitations-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>

    async cancelInvitation(invitation) {
      if (!confirm(`Cancel invitation for ${invitation.username}?`)) return;

      this.cancellingInvitation = true;

      try {
        const response = await axios.delete(
          `/api/v1/njangi/groups/${this.selectedGroup.id}/invitations/${invitation.id}/cancel/`
        );

        if (response.data.success) {
          this.showSnackbar(response.data.message, 'success');
          await this.loadGroupInvitations();
        }
      } catch (error) {
        console.error('Cancel invitation error:', error);
        const errorMsg = error.response?.data?.error || 'Error cancelling invitation';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.cancellingInvitation = false;
      }
    },

    async resendInvitation(invitation) {
      this.resendingInvitation = true;

      try {
        const response = await axios.post(
          `/api/v1/njangi/groups/${this.selectedGroup.id}/invitations/${invitation.id}/resend/`
        );

        if (response.data.success) {
          this.showSnackbar(response.data.message, 'success');
        }
      } catch (error) {
        console.error('Resend invitation error:', error);
        const errorMsg = error.response?.data?.error || 'Error resending invitation';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.resendingInvitation = false;
      }
    },

    // Nouveau système de gestion des positions
    openPositionDialog(member) {
      this.selectedMember = member;
      this.newPosition = member.position;
      this.positionError = "";
      this.positionDialog = true;
    },

    closePositionDialog() {
      this.positionDialog = false;
      this.selectedMember = null;
      this.newPosition = null;
      this.positionError = "";
    },

    getPositionTaker() {
      if (!this.newPosition) return null;
      return this.groupMembers.find(m =>
        m.position === parseInt(this.newPosition) && m.id !== this.selectedMember?.id
      );
    },

    async updateMemberPosition() {
      // Validation
      if (!this.newPosition || this.newPosition < 1 || this.newPosition > this.groupMembers.length) {
        this.positionError = `Position must be between 1 and ${this.groupMembers.length}`;
        return;
      }

      this.positionError = "";
      this.updatingPosition = true;

      try {
        const response = await axios.post('/api/v1/manager/njangi/update-member-position/', {
          group_id: this.selectedGroup.id,
          member_id: this.selectedMember.user_id,
          old_position: this.selectedMember.position,
          new_position: parseInt(this.newPosition)
        });

        this.showSnackbar(response.data.message || "Position updated successfully", 'success');

        // Refresh data
        await this.loadGroupMembers();

        this.closePositionDialog();

      } catch (error) {
        this.positionError = error.response?.data?.error || "Error updating position";
        this.showSnackbar(this.positionError, 'error');
      } finally {
        this.updatingPosition = false;
      }
    },

    confirmRemoveMemberFromPosition(member) {
      this.memberToRemovePosition = member;
      this.showRemoveMemberPositionDialog = true;
    },

    async removeMemberFromPosition() {
      this.removingMemberPosition = true;

      try {
        const response = await axios.post('/api/v1/manager/njangi/remove-member/', {
          group_id: this.selectedGroup.id,
          member_id: this.memberToRemovePosition.user_id,
          position: this.memberToRemovePosition.position
        });

        this.showSnackbar(response.data.message || "Member removed successfully", 'success');

        await this.loadGroupMembers();

        this.showRemoveMemberPositionDialog = false;
        this.memberToRemovePosition = null;

      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error removing member';
        this.showSnackbar(errorMsg, 'error');
      } finally {
        this.removingMemberPosition = false;
      }
    },

    confirmRemoveMember(member) {
      this.memberToRemove = member;
      this.showRemoveDialog = true;
    },

    async removeMember() {
      this.removingMember = true;
      try {
        await axios.delete(`/api/v1/njangi/groups/${this.selectedGroup.id}/members/${this.memberToRemove.user_id}/`);

        this.showSnackbar('Member removed successfully', 'success');
        this.showRemoveDialog = false;

        await this.loadGroupMembers();
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error removing member';
        this.showSnackbar(errorMsg, 'error');
        console.error('Remove member error:', error);
      } finally {
        this.removingMember = false;
        this.memberToRemove = null;
      }
    },

    async searchUsers() {
      if (!this.searchUsername || this.searchUsername.length < 2) {
        this.searchResults = [];
        return;
      }

      try {
        const response = await axios.get('/api/v1/njangi/search-users/', {
          params: { q: this.searchUsername }
        });
        this.searchResults = response.data;
      } catch (error) {
        console.error('Search error:', error);
        this.searchResults = [];
      }
    },

    async inviteUser(user) {
      try {
        await axios.post(`/api/v1/njangi/groups/${this.selectedGroup.id}/invite/`, {
          user_id: user.id,
          username: user.username
        });

        this.showSnackbar(`Invitation sent to ${user.username}`, 'success');
        this.searchUsername = '';
        this.searchResults = [];
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error sending invitation';
        this.showSnackbar(errorMsg, 'error');
      }
    },

    copyInviteLink(link) {
      navigator.clipboard.writeText(link);
      this.showSnackbar('Link copied to clipboard', 'success');
    },

    shareViaWhatsApp(group) {
      const message = `🎯 Join my Njangi group *${group.name}* on PettyCash!\n\n💰 Contribution: ${group.contribution_amount.toLocaleString()} FCFA\n📅 Cycle: ${group.collecting_cycle}\n\n👉 Click here to join: ${group.invite_link}`;
      const whatsappLink = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappLink, '_blank');
    },

    showSnackbar(message, type = 'info') {
      try {
        this.$store.dispatch('snackbar/showMessage', {
          text: message,
          color: type === 'success' ? '#00479b' : '#d32f2f'
        });
      } catch (e) {
        if (this.$root.$emit) {
          this.$root.$emit('show-snackbar', {
            message: message,
            type: type
          });
        }
        console.log(`${type.toUpperCase()}: ${message}`);
      }
    }
  }
};
</script>

<style scoped>
.manage-admin-groups-wrapper {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
  min-height: 100vh;
}

.mobile-header {
  position: sticky;
  top: 0;
  z-index: 100;
}

.content-container {
  max-width: 1200px;
  margin: 0 auto;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.section-title {
  padding: 16px;
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 71, 155, 0.1);
}

.groups-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

@media (max-width: 600px) {
  .groups-list {
    grid-template-columns: 1fr;
  }
}

.group-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.group-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 71, 155, 0.15);
}

.group-header {
  padding: 20px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.group-icon {
  margin-bottom: 12px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
}

.group-name {
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.group-details {
  padding: 16px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.875rem;
}

.detail-value {
  font-weight: 500;
  color: #333;
}

.group-actions {
  background-color: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

.share-btn,
.manage-btn {
  border-radius: 8px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

.create-btn {
  border-radius: 12px;
  text-transform: none;
  letter-spacing: normal;
}

/* Dialog Styles */
.dialog-wrapper {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  padding: 20px;
  color: white;
}

.dialog-tabs {
  background: white;
  padding: 0 20px;
}

.management-tabs >>> .v-tabs-bar {
  background-color: transparent;
  height: 48px;
}

.management-tab {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
  color: #666;
  min-width: 0;
  flex: 1;
}

.management-tab.v-tab--active {
  color: #00479b;
}

.dialog-content {
  flex: 1;
  overflow-y: auto;
  background: #f8f9fa;
}

.tab-items {
  background: transparent;
}

.tab-content {
  background: white;
  min-height: 400px;
  border-radius: 12px;
  margin: 16px;
}

.tab-header {
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.members-list {
  max-height: 400px;
  overflow-y: auto;
}

.member-card {
  background: #f8f9fa;
  border-radius: 10px;
  transition: all 0.2s;
}

.member-card:hover {
  background: #f0f7ff;
  border: 1px solid #e3f2fd;
}

.member-name {
  color: #333;
}

/* Styles pour la liste des positions */
.position-header {
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 16px;
}

.instruction-card {
  transition: all 0.3s ease;
}

.instruction-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 71, 155, 0.15);
}

.position-item {
  transition: all 0.3s ease;
  background: white;
}

.position-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 71, 155, 0.2) !important;
  border-color: #00479b !important;
}

.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Invite Link Styles */
.invite-link-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 10px;
}

.link-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.link-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  font-size: 0.875rem;
  color: #333;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-input:focus {
  outline: none;
  border-color: #00479b;
}

.copy-btn {
  position: absolute;
  right: 8px;
  background: white;
}

.whatsapp-share-btn {
  border-radius: 10px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

.search-results {
  max-height: 200px;
  overflow-y: auto;
}

.user-result {
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.user-result:hover {
  background: #f0f7ff;
  border: 1px solid #e3f2fd;
}

/* Invitations Styles */
.invitations-list {
  max-height: 400px;
  overflow-y: auto;
}

.invitation-card {
  transition: all 0.2s;
}

.invitation-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Remove Dialog */
.remove-dialog {
  background: white;
  border-radius: 16px;
  overflow: hidden;
}

.remove-dialog .dialog-header {
  padding: 20px;
}

.remove-dialog .dialog-content {
  background: white;
}

.remove-dialog .dialog-actions {
  display: flex;
  gap: 12px;
  background: #f8f9fa;
}

/* Custom Scrollbar */
.members-list::-webkit-scrollbar,
.search-results::-webkit-scrollbar,
.invitations-list::-webkit-scrollbar {
  width: 6px;
}

.members-list::-webkit-scrollbar-track,
.search-results::-webkit-scrollbar-track,
.invitations-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.members-list::-webkit-scrollbar-thumb,
.search-results::-webkit-scrollbar-thumb,
.invitations-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.members-list::-webkit-scrollbar-thumb:hover,
.search-results::-webkit-scrollbar-thumb:hover,
.invitations-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>